import { useEffect, useState } from 'react'

/** Hvor længe der højst går mellem to spørgsmål efter en ny version. */
const CHECK_INTERVAL_MS = 15 * 60 * 1000

/** Der spørges ikke oftere end det her, uanset hvor tit appen hentes frem. */
const MIN_BETWEEN_CHECKS_MS = 60 * 1000

/**
 * "Ny version klar" (beslutning 099).
 *
 * <p>
 * <b>Appen på hjemmeskærmen opdagede ikke selv en udrulning.</b> Service workeren
 * spurgte kun efter en ny version, når siden blev indlæst - så en telefon, der
 * bare lå i lommen, kunne køre en uges gammel version, og en rettelse så ud til
 * ikke at virke. TV-skærmene har haft det her siden beslutning 076; det er den
 * samme mekanik, bare uden at rive brugeren ud af det, han står i.
 * </p>
 *
 * <p>
 * <b>Derfor en bjælke og ikke en genindlæsning.</b> En montør, der står midt i en
 * servicerapport eller en underskrift, ville miste det skrevne, hvis siden hentede
 * sig selv igen under fingrene på ham. Han trykker selv, når det passer.
 * </p>
 *
 * <p>
 * Der spørges, når appen hentes frem på skærmen, og hvert kvarter imens. Service
 * workeren bruger <c>skipWaiting</c> + <c>clientsClaim</c>, så den nye version
 * tager over af sig selv - <c>controllerchange</c> er beskeden om, at der ER
 * noget nyt at vise. Ingen bjælke ved den allerførste installation: der er intet
 * nyt, appen er bare kommet i gang.
 * </p>
 */
export function UpdateBanner() {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return
    }

    const serviceWorker = navigator.serviceWorker
    // Kørte siden ikke i forvejen under en service worker, er det den foerste
    // installation - ikke en ny version.
    const hadController = Boolean(serviceWorker.controller)
    let lastCheck = 0

    function check() {
      const now = Date.now()
      if (now - lastCheck < MIN_BETWEEN_CHECKS_MS) return
      lastCheck = now

      // Fejler opslaget (offline, eller browseren har smidt registreringen
      // vaek), er der ikke noget at goere - saa proeves der bare igen senere.
      void serviceWorker.getRegistration().then((registration) => registration?.update()).catch(() => {})
    }

    function onControllerChange() {
      if (hadController) setReady(true)
    }

    function onVisible() {
      if (document.visibilityState === 'visible') check()
    }

    serviceWorker.addEventListener('controllerchange', onControllerChange)
    document.addEventListener('visibilitychange', onVisible)
    const timer = window.setInterval(check, CHECK_INTERVAL_MS)
    check()

    return () => {
      serviceWorker.removeEventListener('controllerchange', onControllerChange)
      document.removeEventListener('visibilitychange', onVisible)
      window.clearInterval(timer)
    }
  }, [])

  if (!ready) return null

  return (
    <div className="banner banner--update">
      <div style={{ marginRight: 'auto' }}>
        <strong>Ny version klar</strong>
        <div className="small">Tryk for at hente den. Det, du er i gang med, skal gemmes først.</div>
      </div>
      <button className="btn btn--sm btn--primary" onClick={() => window.location.reload()}>
        Opdatér
      </button>
    </div>
  )
}
